import { useApi } from '~/api';
import type { components } from '~~/api-types';

export type PriceTier = components['schemas']['PriceTierDto'];
export type PrintMedium = components['schemas']['PrintMediumResponseDto'];
export type PrintContentListResponse =
  components['schemas']['PaginatedPrintableContentResponseDto'];
export type PrintCheckoutSessionDto =
  components['schemas']['CheckoutSessionResponseDto'];
export type CreatePrintCheckoutSessionBodyDto =
  components['schemas']['CreatePrintCheckoutSessionBodyDto'];
export type BusinessShippingConfigResponse =
  components['schemas']['BusinessShippingConfigResponseDto'];
export type PrintOrder = components['schemas']['OrderResponseDto'];
export type PaginatedOrdersResponse =
  components['schemas']['PaginatedOrdersResponseDto'];
export type AddressValidationResponse = {
  resolved: boolean;
  resolvedAddress?: {
    streetLines: string[];
    city: string;
    stateOrProvinceCode: string;
    postalCode: string;
  };
  notifications?: Array<{
    message: string;
  }>;
};
export type ShippingRate = {
  serviceType: string;
  serviceName: string;
  transitDays: string;
  price: number;
};
export type PrintBudgetSummary =
  components['schemas']['PrintBudgetSummaryResponseDto'];
export type PrintBudgetPaymentRequest =
  components['schemas']['PrintBudgetPaymentBodyDto'];
export type PrintBudgetPaymentResponse =
  components['schemas']['PrintBudgetPaymentResponseDto'];

export type GenerateCustomPdfBody = Omit<
  components['schemas']['GenerateCustomPdfBodyDto'],
  'customizationData'
> & {
  customizationData: {
    logos: Array<{
      x: number;
      y: number;
      width: number;
      height: number;
      quadrantId: number;
    }>;
    currentPage: number;
    logoId: string;
    isPersonalized: boolean;
  };
};
export type GenerateCustomPdfResponse =
  components['schemas']['GenerateCustomPdfResponseDto'];

export const apiGetPrintMediums = (): Promise<PrintMedium[]> => {
  const api = useApi();
  return api('/app/print/mediums', {
    method: 'GET',
  });
};

export const apiGetPrintContentsList = (params?: {
  page?: number;
  perPage?: number;
  search?: string;
  printMediumIds?: number[];
  tagsIds?: number[];
}): Promise<PrintContentListResponse> => {
  const api = useApi();
  return api('/app/print/contents', {
    method: 'GET',
    params,
  });
};

export const apiGetPrintHistory = (params?: {
  page?: number;
  perPage?: number;
}): Promise<PaginatedOrdersResponse> => {
  const api = useApi();
  return api('/app/print/history', {
    method: 'GET',
    params,
  });
};

export const apiCreatePrintCheckoutSession = (
  body: CreatePrintCheckoutSessionBodyDto,
): Promise<PrintCheckoutSessionDto> => {
  const api = useApi();
  return api('/app/print/checkout', {
    method: 'POST',
    body,
  });
};

export const apiGenerateCustomPdf = (
  body: GenerateCustomPdfBody,
): Promise<GenerateCustomPdfResponse> => {
  const api = useApi();
  return api('/app/print/generate-custom-pdf', {
    method: 'POST',
    body,
  });
};

export const apiCheckPrintAvailability = (): Promise<{
  available: boolean;
}> => {
  const api = useApi();
  return api('/app/print/availability', {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });
};

export const apiValidateAddress = (address: {
  line1: string;
  line2?: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
}): Promise<AddressValidationResponse> => {
  const api = useApi();
  return api('/api/shipping/validate-address', {
    method: 'POST',
    body: { address },
  });
};

export const apiGetShippingRates = (params: {
  toAddress: {
    postalCode: string;
    country: string;
  };
  weight: number;
  items?: Array<{ id: string; weight: number; quantity: number }>;
}): Promise<{ rates: ShippingRate[] }> => {
  const api = useApi();
  return api('/app/shipping/rates', {
    method: 'POST',
    body: params,
  });
};

export const apiGetPrintBudgetSummary = (): Promise<PrintBudgetSummary> => {
  const api = useApi();
  return api('/app/budgets/print/summary', {
    method: 'GET',
  });
};

export const apiCreatePrintBudgetPayment = (
  body: PrintBudgetPaymentRequest,
): Promise<PrintBudgetPaymentResponse> => {
  const api = useApi();
  return api('/app/orders/print-budget-payment', {
    method: 'POST',
    body,
  });
};

export const apiGetBusinessShippingConfig =
  (): Promise<BusinessShippingConfigResponse> => {
    const api = useApi();
    return api('/app/print/business-shipping-config', {
      method: 'GET',
    });
  };
